import { parseAmountToCents } from './money'
import { validateIban } from './validators/iban'
import {
  looksStructured,
  validateStructuredCommunication,
} from './validators/structuredCommunication'

/**
 * What a hand-typed payment has to get right, and nothing else.
 *
 * Split from manual.ts deliberately: that module imports the Supabase client,
 * which throws at import time when the environment is not configured — so a
 * unit test of pure validation could not load it. Validation that needs a
 * database connection to be tested is validation that will not be tested.
 */

export interface ManualPayment {
  spaceId: string
  description: string
  amount: string
  dueDate: string
  category: string | null
  iban: string
  reference: string
  notes: string
}

export interface ManualProblem {
  field: keyof ManualPayment
  message: string
}

/** Validated here rather than at submit, so the form can say so as you type. */
export function checkManual(input: ManualPayment): ManualProblem[] {
  const problems: ManualProblem[] = []

  if (!input.description.trim()) {
    problems.push({ field: 'description', message: 'Wie moet betaald worden?' })
  }

  const cents = parseAmountToCents(input.amount)
  if (cents === null || cents <= 0) {
    problems.push({ field: 'amount', message: 'Vul een bedrag in.' })
  }

  if (!/^\d{4}-\d{2}-\d{2}$/.test(input.dueDate)) {
    problems.push({ field: 'dueDate', message: 'Vul een vervaldatum in.' })
  }

  // An IBAN is optional — plenty of things are recorded before you know where
  // to pay them. But a wrong one is never accepted quietly.
  if (input.iban.trim() && !validateIban(input.iban).valid) {
    problems.push({ field: 'iban', message: 'Dit rekeningnummer klopt niet.' })
  }

  // Free text is fine (many suppliers ask for it); a structured communication
  // that fails its own checksum is not.
  if (input.reference.trim() && looksStructured(input.reference)) {
    if (!validateStructuredCommunication(input.reference).valid) {
      problems.push({ field: 'reference', message: 'Deze mededeling klopt niet.' })
    }
  }

  return problems
}

